import styled from '@emotion/styled';
import HomeyLib, { Capability } from 'homey-lib/webpack/index.js';
import { useMemo, useRef } from 'react';
import { useLocale } from 'react-aria';
import semver from 'semver';

import { getCanDisableDefaultDeviceQuickAction } from '../../../lib/deviceQuickAction.ts';

import { HomeyUtils } from '../../../store/HomeyUtils';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useDevice } from '../../../store/devices/useDevices.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';
import { useZonesTree } from '../../../store/zones/useZones.ts';

import { su } from '../../../theme/functions/su';
import { HomeyImages } from '../../../theme/HomeyImages';
import { deviceIconsByCategory } from '../../../theme/HomeyImages';

import { DeviceTile } from '../../../components/device/DeviceTile';
import { IndicatorAlarm } from '../../../components/device/IndicatorAlarm';
import { IndicatorAlarmSpecific } from '../../../components/device/IndicatorAlarmSpecific';
import { IndicatorBattery } from '../../../components/device/IndicatorBattery';
import {
  dangerCapabilityIdList,
  defaultCapabilityIdOrderList,
} from '../../../components/device/IndicatorCapability';
import { IndicatorNumber } from '../../../components/device/IndicatorNumber';
import { IndicatorString } from '../../../components/device/IndicatorString';
import { SelectControllable } from '../../../components/forms/select/SelectControllable';
import { TextField } from '../../../components/forms/TextField';

import { DeviceSettingsGeneralIconSelect } from './DeviceSettingsGeneralIconSelect';
import { DeviceSettingsGeneralQuickActionOverrideSelect } from './DeviceSettingsGeneralQuickActionOverrideSelect';
import { DeviceSettingsGeneralStatusIndicatorSelect } from './DeviceSettingsGeneralStatusIndicatorSelect';

const deviceClasses = HomeyLib.Device.getClasses();

export function DeviceSettingsGeneral(props) {
  const { api } = useApi();
  const { device } = useDevice({ deviceId: props.deviceId });
  const { i18n } = useI18n();
  const { locale } = useLocale();
  const zonesTree = useZonesTree();
  const indicatorAndIconOverlayTargetRef = useRef();
  const hasDeviceQuickActionOverride = HomeyUtils.getHasDeviceQuickActionOverride(api?.homey);

  // Return a nested array of device icon options by category
  const deviceIconOptions = useMemo(() => {
    const options = [
      {
        key: 'default',
        label: 'Default',
        children: [{ key: 'default', textValue: 'default', icon: device?.iconObj }],
      },
    ];

    for (const category of deviceIconsByCategory) {
      options.push({
        key: category.id,
        label: category.label,
        children: category.icons.map((icon) => {
          return {
            key: icon.id,
            textValue: icon.id,
            icon: HomeyImages.getIconForDevice(icon.id),
          };
        }),
      });
    }

    return options;
  }, [device?.iconObj]);

  // Return all capabilities that are of type measure, meter, sensor or alarm.
  const selectableCapabilities = useMemo(() => {
    const selectableCapabilities = [];
    const capabilitiesObj = device?.capabilitiesObj;
    // console.log('capabilitiesObj', capabilitiesObj);
    const alarmCapabilityList = dangerCapabilityIdList;

    // Return none if no capabilities are defined.
    if (capabilitiesObj == null) {
      selectableCapabilities.push({
        id: '.none',
        title: i18n.messageFormatter('common.none'),
        default: true,
      });

      return selectableCapabilities;
    }

    function sortCapabilityImportance(capabilities) {
      const order = defaultCapabilityIdOrderList;

      return capabilities
        .filter((item) => order.includes(item))
        .sort((a, b) => (order.indexOf(a) > order.indexOf(b) ? 1 : -1));
    }

    const defaultDeviceCapabilities = sortCapabilityImportance(Object.keys(capabilitiesObj));

    function returnAlarmCapabilities(capabilities) {
      return capabilities
        .filter((item) => item.startsWith('alarm_'))
        .filter((item) => !alarmCapabilityList.includes(item))
        .filter((item) => !Capability.isInstanceOfId(item, 'alarm_battery'));
    }

    const alarmCapabilities = returnAlarmCapabilities(Object.keys(capabilitiesObj));

    if (alarmCapabilities.length >= 1) {
      selectableCapabilities.push({
        id: '.anyAlarm',
        title: i18n.messageFormatter('device.settings.anyAlarm'),
        default: true,
      });
    }

    let batteryCapabilityAdded = false;

    // Iterate over the device capabilities
    for (const [key, item] of Object.entries(capabilitiesObj)) {
      // Skip the 'button' capability
      if (item.id === 'onoff' || item.id === 'button') {
        continue;
      }

      // Check for battery capabilities
      if (
        Capability.isInstanceOfId(key, 'alarm_battery') ||
        Capability.isInstanceOfId(key, 'measure_battery')
      ) {
        if (!batteryCapabilityAdded) {
          // Add a special item that groups battery capabilities
          const isDefault =
            (defaultDeviceCapabilities[0] === 'alarm_battery' ||
              defaultDeviceCapabilities[0] === 'measure_battery') &&
            alarmCapabilities.length <= 0;
          selectableCapabilities.push({
            id: key,
            title: i18n.messageFormatter('device.settings.anyBattery'),
            default: isDefault,
          });
          batteryCapabilityAdded = true;
        }
      }
      // Check for other capabilities
      else if (
        (key.startsWith('measure_') ||
          key.startsWith('meter_') ||
          key.startsWith('alarm_') ||
          key.startsWith('target_')) &&
        !alarmCapabilityList.includes(key) &&
        (item.type === 'number' || item.type === 'boolean')
      ) {
        // Create a new capabilities item with default values
        const capabilitiesItem = { ...item };
        capabilitiesItem.default =
          defaultDeviceCapabilities[0] === item.id && alarmCapabilities.length <= 0;
        selectableCapabilities.push(capabilitiesItem);
      }
      // Check for generic capabilities
      else if (item.type === 'string') {
        // Create a new capabilities item with default values
        const capabilitiesItem = { ...item };
        capabilitiesItem.default = false;
        selectableCapabilities.push(capabilitiesItem);
      }
    }

    selectableCapabilities.push({
      id: '.none',
      title: i18n.messageFormatter('common.none'),
      default: defaultDeviceCapabilities.length <= 0 && alarmCapabilities.length <= 0,
    });

    return selectableCapabilities;
  }, [i18n, device?.capabilitiesObj]);

  // Return a flat array of status indicator options and the default value.
  const { statusIndicatorOptions, statusIndicatorDefaultValue } = useMemo(() => {
    const statusIndicatorOptions = [];

    function getDefault() {
      const capabilitiesObj = device?.capabilitiesObj;
      const uiIndicator = device?.uiIndicator;

      if (capabilitiesObj == null) return '';

      if (typeof uiIndicator === 'string') {
        return uiIndicator;
      }

      const defaultCapability = selectableCapabilities.filter((item) => {
        return item.default;
      })[0];

      if (defaultCapability) {
        return defaultCapability.id;
      }

      return 'none';
    }

    // Check if the device allows custom indicators
    function getIsCustomIndicatorAllowed() {
      const deviceClass = device?.class;
      const deviceVirtualClass = device?.virtualClass;
      const excludedDeviceTypes = ['light', 'speaker', 'thermostat', 'lock'];
      if (deviceVirtualClass) {
        return !excludedDeviceTypes.includes(deviceVirtualClass);
      }
      return !excludedDeviceTypes.includes(deviceClass);
    }

    // If the device does not have selectable capabilities or the device does not allow custom indicators,
    // return an empty array
    if (getIsCustomIndicatorAllowed() === false || selectableCapabilities.length <= 1) {
      return { statusIndicatorOptions: [], statusIndicatorDefaultValue: null };
    }

    for (const capability of selectableCapabilities) {
      switch (true) {
        case capability.id === '.none':
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: null,
          });
          break;
        case capability.id?.startsWith('alarm_battery') ||
          capability.id?.startsWith('measure_battery'):
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: (
              <IndicatorBattery
                device={device}
                capabilityId={capability.id}
                tileSize={DeviceTile.size.medium}
              />
            ),
          });
          break;
        case capability.id?.startsWith('meter_') ||
          capability.id?.startsWith('measure_') ||
          capability.id?.startsWith('target_'):
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: <IndicatorNumber device={device} capabilityId={capability.id} />,
          });
          break;
        case capability.id?.startsWith('alarm_'):
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: (
              <IndicatorAlarmSpecific
                device={device}
                capabilityId={capability.id}
                tileSize={DeviceTile.size.medium}
              />
            ),
          });
          break;
        case capability.id === '.anyAlarm':
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: <IndicatorAlarm device={device} tileSize={DeviceTile.size.medium} />,
          });
          break;
        // Check for generic capabilities
        case capability.type === 'string':
          statusIndicatorOptions.push({
            key: capability.id,
            textValue: capability.title,
            indicator: <IndicatorString device={device} capabilityId={capability.id} />,
          });
          break;
        default:
          break;
      }
    }
    return { statusIndicatorOptions, statusIndicatorDefaultValue: getDefault() };
  }, [device, selectableCapabilities]);

  const { quickActionOptions, quickActionOptionsDefaultValue, shouldRenderQuickActionOverride } =
    useMemo(() => {
      const quickActionCapabilities = [];

      function getQuickActionCapability(capabilityId, capability) {
        if (capability == null) {
          return null;
        }

        if (capability.type !== 'boolean') {
          return null;
        }

        if (capability.setable !== true) {
          return null;
        }

        return {
          id: capabilityId,
          textValue: capability.title ?? capabilityId,
          capabilityObj: capability,
          actionType: capability.getable === true ? 'toggle' : 'button',
        };
      }

      for (const [id, capability] of Object.entries(device?.capabilitiesObj ?? {})) {
        const quickActionCapability = getQuickActionCapability(id, capability);

        if (quickActionCapability != null) {
          quickActionCapabilities.push(quickActionCapability);
        }
      }

      quickActionCapabilities.sort((a, b) => {
        return a.textValue.localeCompare(b.textValue, locale);
      });

      const defaultCapabilityId = device?.ui?.quickAction ?? null;
      const defaultCapabilityObj =
        defaultCapabilityId != null
          ? (device?.capabilitiesObj?.[defaultCapabilityId] ?? null)
          : null;
      const defaultCapability =
        defaultCapabilityId != null
          ? (quickActionCapabilities.find((capability) => {
              return capability.id === defaultCapabilityId;
            }) ??
            getQuickActionCapability(
              defaultCapabilityId,
              device?.capabilitiesObj?.[defaultCapabilityId],
            ))
          : null;
      const canDisableDefaultQuickAction = getCanDisableDefaultDeviceQuickAction({ device });
      const toggleOptions = quickActionCapabilities.filter((capability) => {
        return capability.actionType === 'toggle';
      });
      const buttonOptions = quickActionCapabilities.filter((capability) => {
        return capability.actionType === 'button';
      });
      const defaultCapabilityTextValue =
        defaultCapability?.textValue ?? defaultCapabilityObj?.title ?? defaultCapabilityId;
      const defaultTextValue =
        defaultCapabilityTextValue != null
          ? i18n.messageFormatter('device.settings.general.quickActionDefaultWithTitle', {
              title: defaultCapabilityTextValue,
            })
          : i18n.messageFormatter('device.settings.general.quickActionDefault');
      const defaultOptions = [
        {
          id: '.default',
          textValue: defaultTextValue,
          capabilityObj:
            canDisableDefaultQuickAction === true
              ? (defaultCapability?.capabilityObj ?? null)
              : null,
          actionType:
            canDisableDefaultQuickAction === true ? (defaultCapability?.actionType ?? null) : null,
        },
      ];

      if (canDisableDefaultQuickAction === true) {
        defaultOptions.unshift({
          id: '.none',
          textValue: i18n.messageFormatter('device.settings.general.quickActionDisabled'),
        });
      }
      const quickActionOptions = [
        {
          key: 'default',
          title: null,
          'aria-label': i18n.messageFormatter('device.settings.general.quickAction'),
          children: defaultOptions,
        },
      ];

      if (toggleOptions.length > 0) {
        quickActionOptions.push({
          key: 'toggle',
          title: i18n.messageFormatter('device.settings.general.quickActionToggle'),
          children: toggleOptions,
        });
      }

      if (buttonOptions.length > 0) {
        quickActionOptions.push({
          key: 'button',
          title: i18n.messageFormatter('device.settings.general.quickActionPress'),
          children: buttonOptions,
        });
      }

      return {
        quickActionOptions,
        quickActionOptionsDefaultValue:
          device?.ui?.quickActionOverride === '.none' && canDisableDefaultQuickAction !== true
            ? '.default'
            : (device?.ui?.quickActionOverride ?? '.default'),
        shouldRenderQuickActionOverride:
          canDisableDefaultQuickAction === true || quickActionCapabilities.length > 0,
      };
    }, [device, i18n, locale]);

  // Return a flat array of zone options with the corresponding level and icon
  const zonesOptions = useMemo(() => {
    return (
      zonesTree.tree?.list?.map((zoneTreeData) => {
        const zone = zonesTree.byId[zoneTreeData.id];

        return {
          key: zoneTreeData.id,
          textValue: zone.name,
          level: zoneTreeData.level ?? 0,
          icon: HomeyImages.getIconForZone(zone.icon),
        };
      }) ?? []
    );
  }, [zonesTree.tree, zonesTree.byId]);

  const virtualClassOptions = useMemo(() => {
    const virtualClassOptions = [];
    const allowedVirtualClasses = deviceClasses[device?.class]?.allowedVirtual ?? [];

    if (api == null) {
      return [];
    }

    for (const virtualClass of allowedVirtualClasses) {
      const classInfo = deviceClasses[virtualClass];

      if (
        api.homey.platform === 'local' &&
        classInfo.minCompatibility != null &&
        semver.lt(semver.coerce(api.homey.softwareVersion), classInfo.minCompatibility)
      ) {
        continue;
      }

      virtualClassOptions.push({
        key: virtualClass,
        textValue: deviceClasses[virtualClass]?.title?.[locale] ?? virtualClass,
      });
    }

    if (virtualClassOptions.length > 0) {
      virtualClassOptions.push({
        key: 'default',
        textValue: i18n.messageFormatter('device.settings.noVirtualClass'),
      });
    }

    return virtualClassOptions;
  }, [device?.class, locale, i18n, api]);

  return (
    <S.Root>
      <S.NameTextField
        id="name"
        name="name"
        label={i18n.messageFormatter('device.settings.general.name')}
        type="text"
        orientation="vertical"
        defaultValue={device?.name}
        register={props.register}
        error={props.errors[props.id]}
      />
      <S.SelectWrapper ref={indicatorAndIconOverlayTargetRef}>
        <DeviceSettingsGeneralIconSelect
          id="icon"
          name="icon"
          label={i18n.messageFormatter('device.settings.general.icon')}
          defaultValue={device?.iconOverride ?? 'default'}
          control={props.control}
          error={props.errors[props.id]}
          overlayTargetRef={indicatorAndIconOverlayTargetRef}
          options={deviceIconOptions}
        />
        <DeviceSettingsGeneralStatusIndicatorSelect
          id="statusIndicator"
          name="statusIndicator"
          label={i18n.messageFormatter('device.settings.general.statusIndicator')}
          defaultValue={statusIndicatorDefaultValue}
          control={props.control}
          error={props.errors[props.id]}
          options={statusIndicatorOptions}
        />
      </S.SelectWrapper>

      {hasDeviceQuickActionOverride === true && shouldRenderQuickActionOverride === true ? (
        <S.QuickActionOverrideSelect
          id="quickActionOverride"
          name="quickActionOverride"
          label={i18n.messageFormatter('device.settings.general.quickAction')}
          defaultValue={quickActionOptionsDefaultValue}
          control={props.control}
          error={props.errors[props.id]}
          options={quickActionOptions}
        />
      ) : null}
      {hasDeviceQuickActionOverride === true && shouldRenderQuickActionOverride !== true ? (
        <input type="hidden" defaultValue=".default" {...props.register('quickActionOverride')} />
      ) : null}

      <S.ZoneSelect
        id="zone"
        name="zone"
        label={i18n.messageFormatter('device.settings.general.zone')}
        orientation="vertical"
        styleOption="optionZone"
        defaultValue={device?.zone}
        control={props.control}
        error={props.errors[props.id]}
        options={zonesOptions}
      />

      {virtualClassOptions.length > 0 && (
        <S.VirtualClassSelect
          id="virtualClass"
          name="virtualClass"
          label={i18n.messageFormatter('device.settings.general.pluggedIn')}
          orientation="vertical"
          styleOption="optionVirtualClass"
          defaultValue={device?.virtualClass ?? 'default'}
          control={props.control}
          error={props.errors[props.id]}
          options={virtualClassOptions}
        />
      )}
    </S.Root>
  );
}

function S() {}
DeviceSettingsGeneral.S = S;

S.Root = styled.div`
  margin-top: ${su(4)};
`;

S.NameTextField = styled(TextField)`
  margin-bottom: ${su(2)};
`;

S.SelectWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 4fr;
  grid-gap: ${su(2)};
  align-items: end;
  margin-bottom: ${su(2)};
`;

S.QuickActionOverrideSelect = styled(DeviceSettingsGeneralQuickActionOverrideSelect)`
  margin-bottom: ${su(2)};
`;

S.ZoneSelect = styled(SelectControllable)`
  margin-bottom: ${su(2)};
`;

S.VirtualClassSelect = styled(SelectControllable)`
  margin-bottom: ${su(2)};
`;
